import React, { useState, useEffect } from 'react';
import { ThemeProvider, CssBaseline, Box, Typography, Button, Link as MuiLink } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link as RouterLink } from 'react-router-dom';
import theme, { tokens } from './theme';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import DocumentationPage from './pages/DocumentationPage';
import StatusPage from './pages/StatusPage';
import ExpertsPage from './pages/ExpertsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import EndUserPrivacyPage from './pages/EndUserPrivacyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import GDPRPage from './pages/GDPRPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import NotFoundPage from './pages/NotFoundPage';

// Scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return null;
};

function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 12, sm: 24 },
        left: 0,
        right: 0,
        mx: 'auto',
        width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
        maxWidth: 720,
        zIndex: 9999,
        bgcolor: tokens.bgPaper,
        border: `1px solid ${tokens.border}`,
        borderRadius: `${tokens.radiusCard}px`,
        boxShadow: tokens.shadowLift,
        px: 3,
        py: 2,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
      }}
    >
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', flex: 1 }}>
        We use cookies to improve your experience. By continuing, you agree to our{' '}
        <MuiLink
          component={RouterLink}
          to="/cookies"
          sx={{ color: tokens.copper, '&:hover': { color: tokens.copperDark } }}
        >
          Cookie Policy
        </MuiLink>.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
        <Button
          size="small"
          onClick={handleDecline}
          sx={{
            color: 'text.secondary',
            fontSize: '0.8rem',
            '&:hover': { color: tokens.copper, bgcolor: 'transparent' },
          }}
        >
          Decline
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleAccept}
          sx={{ fontSize: '0.8rem' }}
        >
          Accept
        </Button>
      </Box>
    </Box>
  );
}

export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/docs" element={<DocumentationPage />} />
        {/* Help Center merged into /docs (single docs + help destination) */}
        <Route path="/help" element={<Navigate to="/docs" replace />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/experts" element={<ExpertsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy/end-user" element={<EndUserPrivacyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/gdpr" element={<GDPRPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        {/* Meta App Review "Data Deletion Instructions URL" — renders the
            end-user privacy page (which contains the §10 deletion instructions)
            directly at /data-deletion so the URL is never a 404. */}
        <Route path="/data-deletion" element={<EndUserPrivacyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

export default App;
